import { useEffect, useState, useCallback, useRef } from 'react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { Plus, Trash2, Save, Users, Package, Upload, FileDown } from 'lucide-react';

// Reusable CSV importer: template download + upload + result summary.
function CsvImport({ title, hint, templateHref, endpoint, onDone }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const onFile = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setBusy(true); setResult(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const r = await api.post(endpoint, fd);
      setResult(r.data);
      const summary = r.data.created != null
        ? `${r.data.created} created, ${r.data.updated} updated`
        : `${r.data.added} added, ${r.data.removed} removed`;
      toast.success(`Imported: ${summary}`);
      onDone && onDone();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Import failed');
    } finally { setBusy(false); }
  };
  return (
    <div className="import-row">
      <div style={{ flex: 1, minWidth: 0 }}>
        <strong style={{ fontSize: 13 }}>{title}</strong>
        <p className="form-hint">{hint}</p>
        {result && (
          <div className="import-result">
            <div>{result.processed} row(s) processed{result.created != null
              ? ` — ${result.created} created, ${result.updated} updated`
              : ` — ${result.added} added, ${result.removed} removed`}.</div>
            {result.groupsCreated?.length > 0 && <div>New groups created: {result.groupsCreated.join(', ')}.</div>}
            {result.skipped?.length > 0 && (
              <details>
                <summary>{result.skipped.length} row(s) skipped</summary>
                <ul>{result.skipped.map((s, i) => <li key={i}>Row {s.row}: {s.reason}</li>)}</ul>
              </details>
            )}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'stretch' }}>
        <a className="btn btn-ghost btn-sm" href={templateHref} download><FileDown size={13} /> Template</a>
        <button className="btn btn-secondary btn-sm" onClick={() => ref.current?.click()} disabled={busy}>
          <Upload size={13} /> {busy ? 'Importing…' : 'Upload CSV'}
        </button>
        <input ref={ref} type="file" accept=".csv,text/csv" style={{ display: 'none' }} onChange={onFile} />
      </div>
    </div>
  );
}

export default function GroupsAdminPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [customerId, setCustomerId] = useState(null); // TTC selection
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState({ productIds: [], userIds: [] });
  const [newName, setNewName] = useState('');

  const load = useCallback((cid) => {
    setLoading(true);
    const qs = cid ? `?customerId=${cid}` : '';
    return api.get(`/api/groups${qs}`)
      .then(r => { setData(r.data); setLoading(false); })
      .catch(e => { toast.error(e.response?.data?.error || 'Failed to load'); setLoading(false); });
  }, []);

  useEffect(() => { load(customerId); }, [customerId, load]);

  const selectGroup = (id) => {
    setSelectedId(id);
    api.get(`/api/groups/${id}`)
      .then(r => setDetail({ productIds: r.data.productIds, userIds: r.data.userIds }))
      .catch(() => toast.error('Failed to load group'));
  };

  const refresh = () => load(customerId);

  const setMode = (mode) => {
    api.put('/api/groups/mode', { customer_id: data.customer.id, mode })
      .then(() => { toast.success(`Visibility set to "${mode}"`); refresh(); })
      .catch(e => toast.error(e.response?.data?.error || 'Failed'));
  };

  const createGroup = () => {
    const name = newName.trim();
    if (!name) return;
    api.post('/api/groups', { customer_id: data.customer.id, name })
      .then(r => { setNewName(''); toast.success('Group created'); refresh(); selectGroup(r.data.group.id); })
      .catch(e => toast.error(e.response?.data?.error || 'Failed'));
  };

  const deleteGroup = (id, name) => {
    if (!window.confirm(`Delete group "${name}"? Users in it will lose access to its items.`)) return;
    api.delete(`/api/groups/${id}`)
      .then(() => { toast.success('Group deleted'); if (selectedId === id) setSelectedId(null); refresh(); })
      .catch(e => toast.error(e.response?.data?.error || 'Failed'));
  };

  const toggle = (key, id) => setDetail(d => ({
    ...d,
    [key]: d[key].includes(id) ? d[key].filter(x => x !== id) : [...d[key], id],
  }));

  const saveProducts = () =>
    api.put(`/api/groups/${selectedId}/products`, { product_ids: detail.productIds })
      .then(() => { toast.success('Items saved'); refresh(); })
      .catch(e => toast.error(e.response?.data?.error || 'Failed'));

  const saveUsers = () =>
    api.put(`/api/groups/${selectedId}/users`, { user_ids: detail.userIds })
      .then(() => { toast.success('Users saved'); refresh(); })
      .catch(e => toast.error(e.response?.data?.error || 'Failed'));

  if (loading || !data) return (<><div className="topbar"><h2>Item Groups</h2></div><div className="page"><div className="loading">Loading…</div></div></>);

  // TTC with no customer chosen yet
  if (data.needsCustomer || !data.customer) {
    return (
      <>
        <div className="topbar"><h2>Item Groups</h2></div>
        <div className="page">
          <div className="card" style={{ maxWidth: 460 }}>
            <div className="card-header"><h3>Choose a customer</h3></div>
            <p className="form-hint" style={{ marginBottom: 12 }}>Select which customer's item groups you want to manage.</p>
            <select className="form-select" defaultValue="" onChange={e => setCustomerId(e.target.value || null)}>
              <option value="" disabled>Select a customer…</option>
              {data.customers?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
      </>
    );
  }

  const selected = data.groups.find(g => g.id === selectedId);

  return (
    <>
      <div className="topbar">
        <h2>Item Groups</h2>
        {data.isTTC && (
          <select className="form-select" style={{ width: 240 }} value={data.customer.id}
            onChange={e => { setSelectedId(null); setCustomerId(e.target.value); }}>
            {data.customers?.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        )}
      </div>
      <div className="page">
        {/* Visibility mode */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><h3>Item visibility — {data.customer.name}</h3></div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <input type="radio" name="mode" checked={data.mode === 'all'} onChange={() => setMode('all')} style={{ marginTop: 3 }} />
              <span><strong>Show all items</strong><br /><span className="form-hint">Every user sees all of this customer's items. (Default.)</span></span>
            </label>
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <input type="radio" name="mode" checked={data.mode === 'group'} onChange={() => setMode('group')} style={{ marginTop: 3 }} />
              <span><strong>Restrict by group</strong><br /><span className="form-hint">A user only sees items in the groups they're assigned to. Users with no group see no items.</span></span>
            </label>
          </div>
        </div>

        {/* Bulk import */}
        <div className="card" style={{ marginBottom: 20 }}>
          <div className="card-header"><h3>Bulk import (CSV)</h3></div>
          <CsvImport
            title="Users & group assignments"
            hint="Columns: customer_slug, email, first_name, last_name, role, password, groups, ship_tos. Password can be blank for SSO/Office 365 users. Separate multiple groups with semicolons; unknown groups are created automatically. ship_tos is a semicolon-separated list of ship-to nicknames that already exist — leave it blank to let the user ship to every address. Existing users (matched by email) are updated."
            templateHref="/users_import_template.csv"
            endpoint="/api/groups/import/users"
            onDone={refresh}
          />
          <CsvImport
            title="Item → group assignments"
            hint="Columns: customer_slug, group, sku, action (add or remove; defaults to add). SKUs must match items that are assignable to the customer. Unknown groups are created automatically."
            templateHref="/item_groups_import_template.csv"
            endpoint="/api/groups/import/item-groups"
            onDone={refresh}
          />
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 20, alignItems: 'start' }}>
          {/* Group list */}
          <div className="card">
            <div className="card-header"><h3>Groups</h3></div>
            <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
              <input className="form-input" placeholder="New group name" value={newName}
                onChange={e => setNewName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && createGroup()} />
              <button className="btn btn-primary btn-sm" onClick={createGroup}><Plus size={14} /></button>
            </div>
            {data.groups.length === 0 && <p className="text-muted">No groups yet.</p>}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {data.groups.map(g => (
                <button key={g.id} onClick={() => selectGroup(g.id)}
                  className={'group-row' + (g.id === selectedId ? ' active' : '')}>
                  <span>{g.name}</span>
                  <span className="group-counts"><Package size={12} /> {g.product_count} &nbsp; <Users size={12} /> {g.user_count}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Group editor */}
          <div className="card">
            {!selected ? (
              <p className="text-muted">Select a group to assign items and users, or create one.</p>
            ) : (
              <>
                <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3>{selected.name}</h3>
                  <button className="btn btn-ghost btn-sm" onClick={() => deleteGroup(selected.id, selected.name)}>
                    <Trash2 size={14} /> Delete group
                  </button>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
                  {/* Items */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <strong style={{ fontSize: 13 }}>Items in this group</strong>
                      <button className="btn btn-primary btn-sm" onClick={saveProducts}><Save size={13} /> Save</button>
                    </div>
                    <div className="checklist">
                      {data.products.length === 0 && <p className="text-muted">No assignable items for this customer.</p>}
                      {data.products.map(p => (
                        <label key={p.id} className="check-item">
                          <input type="checkbox" checked={detail.productIds.includes(p.id)} onChange={() => toggle('productIds', p.id)} />
                          <span>{p.name} <span className="text-muted">({p.sku})</span></span>
                        </label>
                      ))}
                    </div>
                  </div>
                  {/* Users */}
                  <div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                      <strong style={{ fontSize: 13 }}>Users in this group</strong>
                      <button className="btn btn-primary btn-sm" onClick={saveUsers}><Save size={13} /> Save</button>
                    </div>
                    <div className="checklist">
                      {data.users.length === 0 && <p className="text-muted">No users for this customer.</p>}
                      {data.users.map(u => (
                        <label key={u.id} className="check-item">
                          <input type="checkbox" checked={detail.userIds.includes(u.id)} onChange={() => toggle('userIds', u.id)} />
                          <span>{u.first_name} {u.last_name} <span className="text-muted">({u.role.replace(/_/g, ' ')})</span></span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
